import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { orderLangLabel, vendorCurrency } from '../constants';
import { initAudioUnlock, playOrderAlertChime } from '../lib/orderAlerts';
import { syncLpPushSubscription } from '../lib/lpPush';

const POLL_MS = 10000;
const NOTIF_NUDGE_KEY = 'lp_notif_nudge_dismissed_v1';
const FINAL_STATUSES = new Set(['completed', 'cancelled', 'canceled']);

const STATUS_STYLE = {
  waiting_price: {
    border: '#f59e0b',
    bg: '#fffbeb',
    chip: 'warning',
    hint: 'Needs price',
  },
  priced: {
    border: '#fb923c',
    bg: '#fff7ed',
    chip: 'warning',
    hint: 'Price sent to resident — waiting confirmation',
    hintColor: '#15803d',
  },
  modify_requested: {
    border: '#8b5cf6',
    bg: '#f5f3ff',
    chip: 'secondary',
    hint: 'Resident asked a change',
  },
  confirmed: {
    border: '#14b8a6',
    bg: '#f0fdfa',
    chip: 'info',
    hint: 'Confirmed — Take & pack',
  },
  preparing: {
    border: '#d97706',
    bg: '#fffbeb',
    chip: 'warning',
    hint: 'Packing',
  },
  on_the_way: {
    border: '#3b82f6',
    bg: '#eff6ff',
    chip: 'info',
    hint: 'On the way',
  },
  completed: {
    border: '#22c55e',
    bg: '#f0fdf4',
    chip: 'success',
    hint: 'Delivered',
  },
  cancelled: {
    border: '#94a3b8',
    bg: '#f8fafc',
    chip: 'default',
    hint: 'Cancelled',
  },
  canceled: {
    border: '#94a3b8',
    bg: '#f8fafc',
    chip: 'default',
    hint: 'Cancelled',
  },
};

function normalizeStatus(status) {
  return String(status || 'waiting_price').toLowerCase();
}

function statusMeta(status) {
  const s = normalizeStatus(status);
  return (
    STATUS_STYLE[s] || {
      border: '#94a3b8',
      bg: '#fff',
      chip: 'default',
      hint: String(status || 'pending').replace(/_/g, ' '),
    }
  );
}

function normalizeOrders(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.orders)) return data.orders;
  return [];
}

function itemLabel(it) {
  return it?.description || it?.name || it?.product_name || 'Item';
}

function formatDeliveryAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function deliveryTimeMeta(order) {
  const at = order?.requested_delivery_at;
  if (!at) return null;
  const status = String(order?.delivery_time_status || '').toLowerCase();
  const by = String(order?.delivery_time_proposed_by || '').toLowerCase();
  return { at, status, by, label: formatDeliveryAt(at) };
}

function canEditDeliveryTime(status) {
  const s = normalizeStatus(status);
  return !['on_the_way', 'completed', 'cancelled', 'canceled'].includes(s);
}

function toLocalDateValue(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toLocalTimeValue(d = new Date()) {
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${min}`;
}

function combineLocalDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function isStandalonePwa() {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    /* ignore */
  }
  return navigator.standalone === true;
}

export default function OrdersPage() {
  const { vendor } = useAuth();
  const currency = vendorCurrency(vendor);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiMissing, setApiMissing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [priceDraft, setPriceDraft] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState('');
  const [itemAction, setItemAction] = useState(null);
  const [itemReply, setItemReply] = useState('');
  const [itemBusy, setItemBusy] = useState(false);
  const [itemError, setItemError] = useState('');
  const [showNotifNudge, setShowNotifNudge] = useState(false);
  const [showNotifSetup, setShowNotifSetup] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  // POD (proof of delivery) — 3 options only; real code is RST-only
  const [podOrderId, setPodOrderId] = useState(null);
  const [podLoading, setPodLoading] = useState(false);
  const [podOptions, setPodOptions] = useState([]);
  const [podFails, setPodFails] = useState(0);
  const [podMaxFails, setPodMaxFails] = useState(3);
  const [podException, setPodException] = useState('');
  const [podNote, setPodNote] = useState('');
  const [podError, setPodError] = useState('');
  const [podSubmitting, setPodSubmitting] = useState(false);
  const [collapsedOrders, setCollapsedOrders] = useState(() => new Set());
  const [previewImage, setPreviewImage] = useState(null);
  const knownIds = useRef(new Set());
  const primed = useRef(false);
  // Auto-collapse each final-status order only once so silent polls do not
  // re-collapse cards the shop just expanded (same pattern as partner orders).
  const autoCollapsedFinalIdsRef = useRef(new Set());

  const POD_EXCEPTION_OPTIONS = [
    { value: 'resident_refused', label: 'Resident refused / no answer' },
    { value: 'no_code_in_app', label: 'Resident has no code in app' },
    { value: 'left_agreed', label: 'Left as agreed with resident' },
    { value: 'wrong_apartment_returned', label: 'Wrong apartment — returned' },
    { value: 'other', label: 'Other (note required)' },
  ];

  useEffect(() => {
    initAudioUnlock();
    // Re-register Web Push endpoint after login (hotel pattern).
    void syncLpPushSubscription();
  }, []);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(NOTIF_NUDGE_KEY) === '1';
      const softEnabled = localStorage.getItem('lp_notif_enabled_v1') === '1';
      const perm =
        typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      const granted = perm === 'granted' || softEnabled;
      // Hide all setup notes once granted, soft-enabled, or dismissed (iPhone XR bug fix).
      if (granted || dismissed) {
        setShowNotifNudge(false);
        setShowNotifSetup(false);
        return;
      }
      const notifDefault = perm === 'default';
      if (isStandalonePwa() && notifDefault) {
        setShowNotifNudge(true);
        setShowNotifSetup(false);
      } else {
        setShowNotifNudge(false);
        setShowNotifSetup(true);
      }
    } catch {
      setShowNotifNudge(false);
      setShowNotifSetup(false);
    }
  }, []);

  const dismissNotifNudge = () => {
    try {
      localStorage.setItem(NOTIF_NUDGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setShowNotifNudge(false);
    setShowNotifSetup(false);
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/towers/shop/orders');
      const list = normalizeOrders(res.data);
      setApiMissing(false);
      setError('');

      if (primed.current) {
        const fresh = list.filter((o) => o?.id && !knownIds.current.has(o.id));
        if (fresh.length) {
          playOrderAlertChime();
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              const first = fresh[0];
              const code = first?.order_code || first?.code || 'New order';
              new Notification('New Neighbour order', {
                body: String(code),
                tag: `lp-order-${first?.id || code}`,
                icon: '/icon-192.png',
              });
            } catch {
              /* ignore */
            }
          }
        }
      }
      knownIds.current = new Set(list.map((o) => o.id).filter(Boolean));
      primed.current = true;
      setOrders(list);
      return list;
    } catch (err) {
      if (err?.response?.status === 404) {
        setApiMissing(true);
        setOrders([]);
        setError('');
      } else {
        setError(formatApiError(err, 'Could not load resident orders'));
      }
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Default-collapse completed/cancelled once; keep user expand across polls
  useEffect(() => {
    if (!Array.isArray(orders)) return;
    setCollapsedOrders((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const o of orders) {
        const id = o?.id;
        if (id == null) continue;
        const status = normalizeStatus(o.status);
        if (
          FINAL_STATUSES.has(status) &&
          !autoCollapsedFinalIdsRef.current.has(id)
        ) {
          autoCollapsedFinalIdsRef.current.add(id);
          if (!next.has(id)) {
            next.add(id);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [orders]);

  const isCollapsed = (id) => collapsedOrders.has(id);
  const toggleCollapsed = (id) => {
    if (id == null) return;
    setCollapsedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyDetail = (order) => {
    if (!order) return;
    setDetail(order);
    setPriceDraft(
      order?.total_amount != null
        ? String(order.total_amount)
        : order?.quoted_total != null
          ? String(order.quoted_total)
          : order?.total != null
            ? String(order.total)
            : priceDraft
    );
    if (order?.requested_delivery_at) {
      const existing = new Date(order.requested_delivery_at);
      if (!Number.isNaN(existing.getTime())) {
        setDeliveryDate(toLocalDateValue(existing));
        setDeliveryTime(toLocalTimeValue(existing));
      }
    }
  };

  const patchOrder = async (id, body) => {
    setBusyId(id);
    setError('');
    setSuccessMsg('');
    try {
      const res = await api.patch(`/api/towers/shop/orders/${id}`, body);
      const updated = res?.data && typeof res.data === 'object' ? res.data : null;
      const list = await load({ silent: true });
      if (detail?.id === id) {
        if (updated && (updated.id || updated.status || updated.items || updated.line_items || updated.requested_delivery_at !== undefined)) {
          applyDetail({ ...detail, ...updated });
        } else {
          const fromList = Array.isArray(list) ? list.find((o) => o?.id === id) : null;
          if (fromList) applyDetail(fromList);
          else setDetail((d) => (d ? { ...d, ...body } : d));
        }
      }
      if (body?.status === 'priced') {
        setSuccessMsg('Price sent to resident.');
        setDetail(null);
      }
      return true;
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('Order update API is not available yet. Try again after the next backend deploy.');
      } else {
        setError(formatApiError(err, 'Could not update order'));
      }
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const ownerId = vendor?.id != null ? String(vendor.id) : null;

  const claimState = (o) => {
    const takenId =
      o?.taken_by_staff_id != null ? String(o.taken_by_staff_id) : null;
    const mine = !!(takenId && ownerId && takenId === ownerId);
    const takenByOther = !!(takenId && (!ownerId || takenId !== ownerId));
    return { takenId, mine, takenByOther };
  };

  const onTakePack = async (o) => {
    setBusyId(o.id);
    try {
      await api.post(`/api/vendor/delivery-staff/orders/${o.id}/take`, null, {
        params: { order_kind: 'tower' },
      });
      setSuccessMsg('Taken — packing');
      await load({ silent: true });
    } catch (err) {
      setError(formatApiError(err, 'Could not take order'));
    } finally {
      setBusyId(null);
    }
  };

  const onOwnerOnTheWay = async (o) => {
    setBusyId(o.id);
    try {
      await api.post(
        `/api/vendor/delivery-staff/orders/${o.id}/on-the-way`,
        null,
        { params: { order_kind: 'tower' } }
      );
      setSuccessMsg('On the way');
      await load({ silent: true });
    } catch (err) {
      setError(formatApiError(err, 'Could not mark on the way'));
    } finally {
      setBusyId(null);
    }
  };

  const onReleaseClaim = async (o) => {
    setBusyId(o.id);
    try {
      await api.post(`/api/vendor/delivery-staff/orders/${o.id}/release`, null, {
        params: { order_kind: 'tower' },
      });
      setSuccessMsg('Released');
      await load({ silent: true });
    } catch (err) {
      setError(formatApiError(err, 'Could not release'));
    } finally {
      setBusyId(null);
    }
  };

  const resetPod = () => {
    setPodOrderId(null);
    setPodOptions([]);
    setPodFails(0);
    setPodException('');
    setPodNote('');
    setPodError('');
    setPodSubmitting(false);
    setPodLoading(false);
  };

  const openPodDialog = async (oOrId) => {
    const o = typeof oOrId === 'object' && oOrId ? oOrId : { id: oOrId };
    const { takenId, mine, takenByOther } = claimState(o);
    if (!takenId) {
      setError('Take & pack the order first, then Confirm delivery');
      return;
    }
    if (takenByOther || !mine) {
      setError(
        o.taken_label ||
          'Another delivery person has this order — monitor only'
      );
      return;
    }
    const id = o.id;
    setPodOrderId(id);
    setPodLoading(true);
    setPodError('');
    setPodException('');
    setPodNote('');
    setPodOptions([]);
    try {
      const res = await api.get(
        `/api/vendor/delivery-staff/orders/${id}/pod-challenge`,
        { params: { order_kind: 'tower' } }
      );
      const challenge = res.data || {};
      setPodOptions(challenge.options || []);
      setPodFails(challenge.fails || 0);
      setPodMaxFails(challenge.max_fails || 3);
    } catch (err) {
      setPodError(formatApiError(err, 'Could not load delivery codes'));
    } finally {
      setPodLoading(false);
    }
  };

  const submitPodCode = async (code) => {
    if (!podOrderId || podSubmitting) return;
    setPodSubmitting(true);
    setPodError('');
    try {
      const confirmedId = podOrderId;
      await api.post(
        `/api/vendor/delivery-staff/orders/${confirmedId}/confirm-delivery`,
        { code, order_kind: 'tower' }
      );
      setSuccessMsg('Order delivered');
      resetPod();
      await load({ silent: true });
      if (detail?.id === confirmedId) setDetail(null);
    } catch (err) {
      const detailErr = err?.response?.data?.detail;
      if (detailErr && typeof detailErr === 'object' && detailErr.message) {
        setPodFails(detailErr.fails || podFails + 1);
        setPodError(
          detailErr.locked
            ? 'Too many wrong attempts — use an exception reason'
            : `Wrong code (${detailErr.fails}/${detailErr.max_fails || podMaxFails})`
        );
        try {
          const res = await api.get(
            `/api/vendor/delivery-staff/orders/${podOrderId}/pod-challenge`,
            { params: { order_kind: 'tower' } }
          );
          setPodOptions(res.data?.options || []);
        } catch {
          /* keep */
        }
      } else {
        setPodError(formatApiError(err, 'Confirm failed'));
      }
    } finally {
      setPodSubmitting(false);
    }
  };

  const submitPodException = async () => {
    if (!podOrderId || !podException || podSubmitting) return;
    if (podException === 'other' && !podNote.trim()) {
      setPodError('Please add a short note for Other');
      return;
    }
    setPodSubmitting(true);
    setPodError('');
    try {
      const confirmedId = podOrderId;
      await api.post(
        `/api/vendor/delivery-staff/orders/${confirmedId}/confirm-delivery`,
        {
          exception_reason: podException,
          note: podNote.trim() || undefined,
          order_kind: 'tower',
        }
      );
      setSuccessMsg('Delivered (exception logged)');
      resetPod();
      await load({ silent: true });
      if (detail?.id === confirmedId) setDetail(null);
    } catch (err) {
      setPodError(formatApiError(err, 'Confirm failed'));
    } finally {
      setPodSubmitting(false);
    }
  };

  const openDetail = (order) => {
    setDetail(order);
    setSuccessMsg('');
    setReportOpen(false);
    setReportText('');
    setReportError('');
    setPriceDraft(
      order?.total_amount != null
        ? String(order.total_amount)
        : order?.quoted_total != null
          ? String(order.quoted_total)
          : order?.total != null
            ? String(order.total)
            : ''
    );
    const existing = order?.requested_delivery_at
      ? new Date(order.requested_delivery_at)
      : null;
    if (existing && !Number.isNaN(existing.getTime())) {
      setDeliveryDate(toLocalDateValue(existing));
      setDeliveryTime(toLocalTimeValue(existing));
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(17, 0, 0, 0);
      setDeliveryDate(toLocalDateValue(tomorrow));
      setDeliveryTime('17:00');
    }
  };

  const proposeDeliveryTime = async () => {
    if (!detail?.id) return;
    const iso = combineLocalDateTime(deliveryDate, deliveryTime);
    if (!iso) {
      setError('Pick a valid delivery date and time.');
      return;
    }
    const ok = await patchOrder(detail.id, {
      delivery_time_action: 'propose',
      requested_delivery_at: iso,
    });
    if (ok) setSuccessMsg('Delivery time sent to resident for confirmation.');
  };

  const confirmResidentDeliveryTime = async () => {
    if (!detail?.id) return;
    const ok = await patchOrder(detail.id, { delivery_time_action: 'confirm' });
    if (ok) setSuccessMsg('Delivery time confirmed.');
  };

  const submitResidentReport = async () => {
    if (!detail?.id) return;
    const reason = reportText.trim();
    if (reason.length < 5) {
      setReportError('Please describe the issue in a few words.');
      return;
    }
    setReportBusy(true);
    setReportError('');
    try {
      await api.post('/api/towers/shop/reports', {
        reason,
        order_id: detail.id,
        reported_resident_id: detail.resident_id || undefined,
      });
      setReportOpen(false);
      setReportText('');
      setSuccessMsg('Report sent. We will investigate and email you.');
    } catch (err) {
      setReportError(formatApiError(err, 'Could not send report'));
    } finally {
      setReportBusy(false);
    }
  };

  const openItemAction = (it) => {
    if (!it?.id) return;
    setItemAction(it);
    setItemReply(it.shop_reply || '');
    setItemError('');
  };

  const applyItemAction = async ({ unavailable, sendReply }) => {
    if (!detail?.id || !itemAction?.id) return;
    const reply = itemReply.trim();
    if (sendReply && reply.length < 2) {
      setItemError('Write a short message for the resident.');
      return;
    }
    setItemBusy(true);
    setItemError('');
    try {
      const body = {
        items: [
          {
            id: itemAction.id,
            unavailable: unavailable ? true : undefined,
            shop_reply: sendReply || unavailable ? reply || undefined : undefined,
          },
        ],
      };
      if (unavailable && !sendReply && !reply) {
        body.items[0].shop_reply = 'Not available';
      }
      const res = await api.patch(`/api/towers/shop/orders/${detail.id}`, body);
      const updated = res?.data && typeof res.data === 'object' ? res.data : null;
      const list = await load({ silent: true });
      if (updated && (updated.id || updated.items || updated.line_items)) {
        applyDetail({ ...detail, ...updated });
      } else {
        const fromList = Array.isArray(list) ? list.find((o) => o?.id === detail.id) : null;
        if (fromList) applyDetail(fromList);
      }
      setSuccessMsg(
        unavailable
          ? 'Marked not available — resident will see your note.'
          : 'Message sent to resident on this order.',
      );
      setItemAction(null);
      setItemReply('');
    } catch (err) {
      setItemError(formatApiError(err, 'Could not update item'));
    } finally {
      setItemBusy(false);
    }
  };

  const renderPrimaryActions = (o) => {
    const status = normalizeStatus(o.status);
    const busy = busyId === o.id;
    const viewBtn = (
      <Button size="small" variant="outlined" onClick={() => openDetail(o)}>
        View
      </Button>
    );

    if (status === 'waiting_price') {
      return (
        <>
          {viewBtn}
          <Button size="small" variant="contained" disabled={busy} onClick={() => openDetail(o)}>
            Set price & send
          </Button>
        </>
      );
    }
    if (status === 'modify_requested') {
      return (
        <>
          {viewBtn}
          <Button size="small" variant="contained" disabled={busy} onClick={() => openDetail(o)}>
            Update price & send
          </Button>
        </>
      );
    }
    if (status === 'priced') {
      return (
        <>
          {viewBtn}
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label="Price sent"
            sx={{ fontWeight: 700, bgcolor: '#dcfce7', color: '#15803d' }}
          />
        </>
      );
    }
    if (status === 'confirmed' || status === 'preparing' || status === 'on_the_way') {
      const { takenId, mine, takenByOther } = claimState(o);
      const onWay = status === 'on_the_way';
      return (
        <>
          {viewBtn}
          {!takenId && (
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => onTakePack(o)}
            >
              Take & pack
            </Button>
          )}
          {mine && !onWay && (
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => onOwnerOnTheWay(o)}
            >
              On the way
            </Button>
          )}
          {mine && (
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => onReleaseClaim(o)}
            >
              Release
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={busy || !mine}
            onClick={() => openPodDialog(o)}
          >
            Confirm delivery
          </Button>
          {takenByOther && (
            <Chip
              size="small"
              label={o.taken_label || `Taken by ${o.taken_by_name || 'staff'}`}
              sx={{
                fontWeight: 700,
                borderColor: '#6366f1',
                color: '#4338ca',
                bgcolor: '#eef2ff',
              }}
              variant="outlined"
            />
          )}
        </>
      );
    }
    // completed / cancelled / unknown — view only
    return viewBtn;
  };

  if (loading && !orders.length && !apiMissing) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const detailStatus = normalizeStatus(detail?.status);
  // Once priced, price is locked — only waiting_price / modify_requested can set a new total.
  const canEditPrice =
    detail && (detailStatus === 'waiting_price' || detailStatus === 'modify_requested');

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Resident orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Neighbour (towers) inbox — refreshes every ~10s. Tap once to enable order chimes.
          </Typography>
          <Chip
            size="small"
            label={`Orders preferred in: ${orderLangLabel(vendor?.neighbour_order_lang)}`}
            sx={{ mt: 1, fontWeight: 700, bgcolor: '#ccfbf1', color: '#0f766e' }}
          />
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => load()} disabled={!!busyId}>
          Refresh
        </Button>
      </Stack>

      {showNotifNudge && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2 }}
          icon={<NotificationsActiveIcon fontSize="inherit" />}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to="/notifications-help"
                onClick={dismissNotifNudge}
                sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                Enable
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Dismiss"
                onClick={dismissNotifNudge}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          App is on your Home Screen. Enable alerts so new orders ping even when you’re in another
          app.
        </Alert>
      )}

      {apiMissing && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Resident order inbox is not live on the API yet. When Neighbour shop orders are enabled,
          new requests will appear here with a chime.
        </Alert>
      )}
      {!apiMissing && showNotifSetup && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2 }}
          icon={<NotificationsActiveIcon fontSize="inherit" />}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to="/notifications-help"
                onClick={dismissNotifNudge}
                sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                Setup
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Dismiss setup note"
                onClick={dismissNotifNudge}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          Want alerts on this phone? Add to Home Screen, enable notifications, or keep this page
          open for the chime.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {!apiMissing && orders.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            No resident orders yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When someone in a linked building orders from your shop on GoSnel Neighbour, it will show
            up here.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {orders.map((o) => {
            const items = o.items || o.line_items || [];
            const status = normalizeStatus(o.status);
            const meta = statusMeta(o.status);
            const collapsed = isCollapsed(o.id);
            return (
              <Paper
                key={o.id || o.order_code}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderLeft: `4px solid ${meta.border}`,
                  bgcolor: meta.bg,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{ mb: collapsed ? 0 : 1 }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography fontWeight={900} noWrap>
                        {o.order_code || o.code || `#${String(o.id || '').slice(0, 8)}`}
                      </Typography>
                      <Chip
                        size="small"
                        color={meta.chip}
                        label={String(status).replace(/_/g, ' ')}
                        sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                      />
                      {(o.taken_label || o.taken_by_name) &&
                        !o.pod_confirmed_at &&
                        !FINAL_STATUSES.has(status) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={o.taken_label || `Taken by ${o.taken_by_name}`}
                            sx={{
                              fontSize: '11px',
                              borderColor: '#6366f1',
                              color: '#4338ca',
                              bgcolor: '#eef2ff',
                            }}
                          />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {[o.resident_name, o.tower_name, o.unit_number || o.apartment]
                        .filter(Boolean)
                        .join(' · ') || 'Resident order'}
                    </Typography>
                    {(o.total_amount != null || o.quoted_total != null || o.total != null) && (
                      <Typography variant="body2" fontWeight={800} sx={{ mt: 0.25 }}>
                        {currency}{' '}
                        {Number(o.total_amount ?? o.quoted_total ?? o.total).toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => toggleCollapsed(o.id)}
                    aria-label={collapsed ? 'Expand order' : 'Collapse order'}
                  >
                    {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                  </IconButton>
                </Stack>

                {!collapsed && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={1.25}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {meta.hint && (
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                          {status === 'priced' && (
                            <CheckCircleIcon
                              sx={{ fontSize: 18, color: meta.hintColor || '#15803d' }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ color: meta.hintColor || 'text.secondary' }}
                          >
                            {meta.hint}
                          </Typography>
                        </Stack>
                      )}

                      {items.length ? (
                        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                          {items.map((it, idx) => {
                            const qty = it.qty || it.quantity || 1;
                            const unavailable = Boolean(it.unavailable);
                            return (
                              <Stack
                                key={it.id || it.product_id || idx}
                                direction="row"
                                spacing={1.25}
                                alignItems="center"
                              >
                                <Box
                                  component={it.image_url ? 'img' : 'div'}
                                  src={it.image_url || undefined}
                                  alt=""
                                  onClick={
                                    it.image_url
                                      ? (e) => {
                                          e.stopPropagation();
                                          setPreviewImage({
                                            url: it.image_url,
                                            label: itemLabel(it),
                                          });
                                        }
                                      : undefined
                                  }
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1.25,
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    bgcolor: '#f1f5f9',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: it.image_url ? 'zoom-in' : 'default',
                                  }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{
                                      textDecoration: unavailable ? 'line-through' : 'none',
                                      color: unavailable ? 'text.secondary' : 'text.primary',
                                    }}
                                  >
                                    {qty}× {itemLabel(it)}
                                    {unavailable ? ' · Not available' : ''}
                                  </Typography>
                                  {it.notes ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {it.notes}
                                    </Typography>
                                  ) : null}
                                </Box>
                                {it.price != null ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                    {currency} {Number(it.price).toFixed(2)}
                                  </Typography>
                                ) : null}
                              </Stack>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          {o.notes || o.message || 'Open for details'}
                        </Typography>
                      )}

                      {(o.total_amount != null || o.quoted_total != null || o.total != null) &&
                        o.delivery_fee_amount != null &&
                        Number(o.delivery_fee_amount) > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                            Products {currency}{' '}
                            {Number(
                              o.product_subtotal ??
                                Number(o.total_amount ?? o.quoted_total ?? o.total) -
                                  Number(o.delivery_fee_amount),
                            ).toFixed(2)}
                            {' · '}Delivery {currency} {Number(o.delivery_fee_amount).toFixed(2)}
                          </Typography>
                        )}

                      {(() => {
                        const dt = deliveryTimeMeta(o);
                        if (!dt) return null;
                        const pendingResident = dt.status === 'pending' && dt.by === 'resident';
                        const pendingShop = dt.status === 'pending' && dt.by === 'shop';
                        const confirmed = dt.status === 'confirmed';
                        return (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mt: 0.5,
                              fontWeight: 700,
                              color: confirmed
                                ? '#15803d'
                                : pendingResident
                                  ? '#c2410c'
                                  : '#0f766e',
                            }}
                          >
                            {confirmed
                              ? `Delivery: ${dt.label}`
                              : pendingResident
                                ? `Resident asked: ${dt.label} — confirm`
                                : pendingShop
                                  ? `Awaiting resident: ${dt.label}`
                                  : `Delivery: ${dt.label}`}
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      {renderPrimaryActions(o)}
                      {status !== 'completed' &&
                        status !== 'cancelled' &&
                        status !== 'canceled' &&
                        status !== 'confirmed' &&
                        status !== 'preparing' &&
                        status !== 'on_the_way' && (
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            disabled={busyId === o.id}
                            onClick={() => {
                              if (window.confirm('Cancel this order?')) {
                                patchOrder(o.id, { status: 'cancelled' });
                              }
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={!!detail}
        onClose={() => {
          setDetail(null);
          setReportOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Order details</DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2">
                <strong>Code:</strong> {detail.order_code || detail.code || detail.id}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {detail.status || 'pending'}
              </Typography>
              <Typography variant="body2">
                <strong>Resident:</strong>{' '}
                {[detail.resident_name, detail.tower_name, detail.unit_number || detail.apartment]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </Typography>
              {detail.notes || detail.message ? (
                <Typography variant="body2">
                  <strong>Notes:</strong> {detail.notes || detail.message}
                </Typography>
              ) : null}
              <Divider />
              <Typography fontWeight={800}>Items</Typography>
              <Typography variant="caption" color="text.secondary">
                Tap an item for quick actions (not available / message resident).
              </Typography>
              {(detail.items || detail.line_items || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No line items listed.
                </Typography>
              ) : (
                (detail.items || detail.line_items).map((it, idx) => {
                  const label = itemLabel(it);
                  const qty = it.qty || it.quantity || 1;
                  const unavailable = Boolean(it.unavailable);
                  return (
                    <Box
                      key={it.id || it.product_id || idx}
                      component="button"
                      type="button"
                      onClick={() => openItemAction(it)}
                      sx={{
                        display: 'flex',
                        width: '100%',
                        textAlign: 'start',
                        gap: 1.25,
                        alignItems: 'flex-start',
                        p: 1,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: unavailable ? '#fecaca' : 'divider',
                        bgcolor: unavailable ? '#fef2f2' : '#fff',
                        cursor: 'pointer',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      <Box
                        component={it.image_url ? 'img' : 'div'}
                        src={it.image_url || undefined}
                        alt=""
                        onClick={
                          it.image_url
                            ? (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setPreviewImage({
                                  url: it.image_url,
                                  label,
                                });
                              }
                            : undefined
                        }
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.5,
                          objectFit: 'cover',
                          flexShrink: 0,
                          bgcolor: '#f1f5f9',
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: it.image_url ? 'zoom-in' : 'default',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {qty}× {label}
                          {unavailable ? ' · Not available' : ''}
                        </Typography>
                        {it.notes ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {it.notes}
                          </Typography>
                        ) : null}
                        {it.shop_reply ? (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.25, color: '#0f766e', fontWeight: 700 }}
                          >
                            To resident: {it.shop_reply}
                          </Typography>
                        ) : null}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {it.price != null ? `${currency} ${Number(it.price).toFixed(2)}` : ''}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {detail.shop_note ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <strong>Note to resident:</strong> {detail.shop_note}
                </Alert>
              ) : null}
              {(() => {
                const dt = deliveryTimeMeta(detail);
                const editable = canEditDeliveryTime(detail.status);
                if (!dt && !editable) return null;
                const pendingResident = dt?.status === 'pending' && dt?.by === 'resident';
                const pendingShop = dt?.status === 'pending' && dt?.by === 'shop';
                const confirmed = dt?.status === 'confirmed';
                return (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: pendingResident ? '#fdba74' : '#cce7e2',
                      bgcolor: pendingResident ? '#fff7ed' : '#f0fdfa',
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>
                      Delivery time (optional)
                    </Typography>
                    {dt ? (
                      <Typography variant="body2" sx={{ mb: 1 }} fontWeight={700}>
                        {confirmed
                          ? `Confirmed: ${dt.label}`
                          : pendingResident
                            ? `Resident requested: ${dt.label}`
                            : pendingShop
                              ? `Waiting for resident: ${dt.label}`
                              : dt.label}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Suggest when you can deliver. Resident will confirm or cancel the order.
                      </Typography>
                    )}
                    {pendingResident && editable ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={busyId === detail.id}
                          onClick={() => confirmResidentDeliveryTime()}
                        >
                          Confirm time
                        </Button>
                      </Stack>
                    ) : null}
                    {editable ? (
                      <Stack spacing={1}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            label="Date"
                            type="date"
                            size="small"
                            value={deliveryDate}
                            onChange={(e) => setDeliveryDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                          <TextField
                            label="Time"
                            type="time"
                            size="small"
                            value={deliveryTime}
                            onChange={(e) => setDeliveryTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Stack>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyId === detail.id || !deliveryDate || !deliveryTime}
                          onClick={() => proposeDeliveryTime()}
                        >
                          {pendingResident
                            ? 'Suggest different time'
                            : dt
                              ? 'Update delivery time'
                              : 'Request delivery time'}
                        </Button>
                      </Stack>
                    ) : null}
                  </Box>
                );
              })()}
              {successMsg && detail ? (
                <Alert severity="success" onClose={() => setSuccessMsg('')}>
                  {successMsg}
                </Alert>
              ) : null}
              {canEditPrice ? (
                <TextField
                  label={`Products total (${currency})`}
                  type="number"
                  value={priceDraft}
                  onChange={(e) => setPriceDraft(e.target.value)}
                  fullWidth
                  helperText={
                    detail.delivery_fee_amount != null && Number(detail.delivery_fee_amount) > 0
                      ? `Enter products only. Delivery ${currency} ${Number(detail.delivery_fee_amount).toFixed(2)} is added automatically.`
                      : 'Set a price, then send it so the resident can confirm.'
                  }
                />
              ) : (
                (detail.total_amount != null ||
                  detail.quoted_total != null ||
                  detail.total != null) && (
                  <Box>
                    {detail.delivery_fee_amount != null && Number(detail.delivery_fee_amount) > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Products: {currency}{' '}
                        {Number(
                          detail.product_subtotal ??
                            Number(detail.total_amount ?? detail.quoted_total ?? detail.total) -
                              Number(detail.delivery_fee_amount),
                        ).toFixed(2)}
                        {' · '}Delivery: {currency}{' '}
                        {Number(detail.delivery_fee_amount).toFixed(2)}
                      </Typography>
                    )}
                    <Typography variant="body2" fontWeight={800}>
                      Total: {currency}{' '}
                      {Number(detail.total_amount ?? detail.quoted_total ?? detail.total).toFixed(2)}
                    </Typography>
                  </Box>
                )
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setDetail(null);
              setReportOpen(false);
            }}
          >
            Close
          </Button>
          {detail?.resident_id || detail?.id ? (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setReportOpen(true);
                setReportError('');
              }}
              sx={{
                color: '#9aa8a6',
                fontSize: 12,
                fontWeight: 500,
                textTransform: 'none',
                minWidth: 0,
                px: 1,
                '&:hover': { bgcolor: 'transparent', color: '#7a8886' },
              }}
            >
              Report resident
            </Button>
          ) : null}
          {canEditPrice && (
            <Button
              variant="contained"
              disabled={!detail || busyId === detail?.id || priceDraft === ''}
              onClick={() =>
                patchOrder(detail.id, {
                  status: 'priced',
                  total_amount: Number(priceDraft),
                  quoted_total: Number(priceDraft),
                })
              }
            >
              {busyId === detail?.id ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                'Send price to resident'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={reportOpen} onClose={() => !reportBusy && setReportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Report resident</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Super Admin will investigate. You and the resident will get an email with your report
            text.
          </Typography>
          <TextField
            label="What happened?"
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            placeholder="e.g. Resident is not answering the phone and address door will not open"
          />
          {reportError ? (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setReportError('')}>
              {reportError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button disabled={reportBusy} onClick={() => setReportOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={reportBusy}
            onClick={() => submitResidentReport()}
          >
            {reportBusy ? <CircularProgress size={18} color="inherit" /> : 'Submit report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!itemAction}
        onClose={() => !itemBusy && setItemAction(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Item actions</DialogTitle>
        <DialogContent>
          <Typography fontWeight={800} sx={{ mb: 0.5 }}>
            {itemAction ? itemLabel(itemAction) : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Like hotel reception reply — tell the resident quickly.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {['Out of stock', 'Can substitute?', 'Slight delay', 'Ask preference'].map((chip) => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                onClick={() => setItemReply(chip)}
                sx={{ fontWeight: 700 }}
              />
            ))}
          </Stack>
          <TextField
            label="Message to resident"
            value={itemReply}
            onChange={(e) => setItemReply(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="e.g. Cola is finished — Pepsi OK?"
          />
          {itemError ? (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setItemError('')}>
              {itemError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button disabled={itemBusy} onClick={() => setItemAction(null)}>
            Close
          </Button>
          <Button
            variant="outlined"
            disabled={itemBusy}
            onClick={() => applyItemAction({ unavailable: false, sendReply: true })}
          >
            {itemBusy ? <CircularProgress size={18} /> : 'Send message'}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={itemBusy}
            onClick={() => applyItemAction({ unavailable: true, sendReply: true })}
          >
            Not available
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!podOrderId}
        onClose={() => !podSubmitting && resetPod()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm delivery</DialogTitle>
        <DialogContent>
          {podLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Ask the resident for their 2-digit code, then tap the matching
                option. Only the resident sees the real code.
              </Typography>
              {podError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {podError}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 1.25,
                  mb: 2,
                }}
              >
                {(podOptions || []).map((opt) => (
                  <Button
                    key={opt}
                    variant="contained"
                    disabled={podSubmitting || podFails >= podMaxFails}
                    onClick={() => submitPodCode(opt)}
                    sx={{
                      py: 1.75,
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      bgcolor: '#0f766e',
                      '&:hover': { bgcolor: '#0d9488' },
                    }}
                  >
                    {String(opt).padStart(2, '0').slice(-2)}
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Or log an exception if the resident has no code:
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Exception reason</InputLabel>
                <Select
                  value={podException}
                  label="Exception reason"
                  onChange={(e) => setPodException(e.target.value)}
                  disabled={podSubmitting}
                >
                  {POD_EXCEPTION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(podException === 'other' || podException) && (
                <TextField
                  fullWidth
                  size="small"
                  label={
                    podException === 'other' ? 'Note (required)' : 'Note (optional)'
                  }
                  value={podNote}
                  onChange={(e) => setPodNote(e.target.value)}
                  disabled={podSubmitting}
                  sx={{ mb: 1 }}
                />
              )}
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                disabled={!podException || podSubmitting}
                onClick={submitPodException}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {podSubmitting ? 'Saving…' : 'Confirm with exception'}
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !podSubmitting && resetPod()}
            disabled={podSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(previewImage?.url)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            m: 1,
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 800, pr: 6 }}>
          {previewImage?.label || 'Item photo'}
          <IconButton
            aria-label="Close"
            onClick={() => setPreviewImage(null)}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 1, sm: 2 },
            minHeight: 240,
          }}
        >
          {previewImage?.url ? (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.label || 'Item photo'}
              sx={{
                maxWidth: '100%',
                maxHeight: '78vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            href={previewImage?.url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!previewImage?.url}
            sx={{ textTransform: 'none', color: '#99f6e4' }}
          >
            Open original
          </Button>
          <Button onClick={() => setPreviewImage(null)} sx={{ textTransform: 'none', color: '#fff' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
